/*
 * Show/hide toggle for every password box. A person somewhere private can
 * reveal what they typed instead of guessing; the default stays masked.
 *
 * Applied to every input[type=password] that lands in the page, so a new form
 * gets it without markup changes. Opt out with data-mcp-no-reveal. The toggle
 * is a real type="button" (never submits) with aria-pressed, and the field is
 * re-masked on submit so the browser never offers to remember it as text.
 *
 * The sign-in page (Mcp.Admin.Api Views/Account/Login.cshtml) cannot load this
 * bundle and carries an inline copy of the same behavior.
 */

const BOUND = 'mcpRevealBound';

const EYE = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">'
    + '<path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8M1.17 8a13 13 0 0 1 1.66-2.04C4.12 4.67 5.88 3.5 8 3.5s3.88 1.17 5.17 2.46A13 13 0 0 1 14.83 8a13 13 0 0 1-1.66 2.04C11.88 11.33 10.12 12.5 8 12.5s-3.88-1.17-5.17-2.46A13 13 0 0 1 1.17 8"/>'
    + '<path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5M4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0"/></svg>';

const EYE_SLASH = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">'
    + '<path d="M13.36 11.24C15.06 9.73 16 8 16 8s-3-5.5-8-5.5a7 7 0 0 0-2.79.59l.77.77A6 6 0 0 1 8 3.5c2.12 0 3.88 1.17 5.17 2.46A13 13 0 0 1 14.83 8q-.09.13-.2.3c-.33.5-.8 1.16-1.43 1.79z"/>'
    + '<path d="M11.3 9.18a3.5 3.5 0 0 0-4.47-4.47l.82.82a2.5 2.5 0 0 1 2.83 2.83zm-2.94 1.29.82.82a3.5 3.5 0 0 1-4.47-4.47l.82.82a2.5 2.5 0 0 0 2.83 2.83"/>'
    + '<path d="M3.35 5.47q-.27.24-.52.49A13 13 0 0 0 1.17 8l.2.3c.33.5.8 1.16 1.43 1.79C4.12 11.33 5.88 12.5 8 12.5c.72 0 1.4-.13 2.02-.36l.77.77A7 7 0 0 1 8 13.5C3 13.5 0 8 0 8s.94-1.73 2.64-3.24l.7.7zm10.3 8.89-12-12 .7-.7 12 12z"/></svg>';

function render(button: HTMLButtonElement, shown: boolean): void {
    button.innerHTML = shown ? EYE_SLASH : EYE;
    const label = shown ? 'Hide password' : 'Show password';
    button.setAttribute('aria-label', label);
    button.title = label;
    button.setAttribute('aria-pressed', String(shown));
}

function bind(input: HTMLInputElement): void {
    if (input.dataset[BOUND] === '1' || input.hasAttribute('data-mcp-no-reveal')) return;
    input.dataset[BOUND] = '1';

    let group = input.parentElement;
    if (!group?.classList.contains('input-group')) {
        group = document.createElement('div');
        group.className = 'input-group mcp-reveal-group';
        if (input.classList.contains('form-control-sm')) group.classList.add('input-group-sm');
        input.before(group);
        group.append(input);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-outline-secondary mcp-reveal';
    if (input.id) button.setAttribute('aria-controls', input.id);
    render(button, false);
    button.addEventListener('click', () => {
        const shown = input.type === 'password';
        input.type = shown ? 'text' : 'password';
        render(button, shown);
    });
    group.append(button);

    input.form?.addEventListener('submit', () => {
        if (input.type !== 'password') {
            input.type = 'password';
            render(button, false);
        }
    }, true);
}

export function enhancePasswordReveal(root: ParentNode = document): void {
    for (const input of Array.from(root.querySelectorAll<HTMLInputElement>('input[type="password"]'))) bind(input);
}
